// « Matériel à chiffrer » d'une variante de la Proposition TRAME (31/10/2026) : ce que la variante pose (unités par modèle, groupes,
// dérivations, cuivre par diamètre, relevage, bus et points GTB), lu par le serveur SANS rien recalculer. Option « Ma gamme » : les
// produits de la société de famille compatible sont PROPOSÉS en premier (« Proposé (Ma gamme) », interne), JAMAIS présélectionnés :
// le choix initial reste le matériel de la variante (décision du 31/10/2026, « on préconise, on propose ») ; le produit Ma gamme ne
// passe au devis (désignation, marque et référence de la société) que si l'utilisateur le choisit. Le serveur n'envoie le fabricant et les prix qu'aux rôles
// autorisés : l'écran n'affiche que ce qu'il reçoit.
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { PackagePlus } from 'lucide-react';
import { api, messageErreur } from '../../../lib/api.ts';
import { euros, nombre } from '../../../lib/format.ts';
import { Alerte, Badge, Bouton, ChampChoix, Champ, Chargement, EtatErreur, Panneau, useNotifier } from '../../../components/ui.tsx';
import { CHOIX_MODELE, choixInitialMateriel, LIBELLE_PROPOSE_GAMME, PHRASE_MARQUE_CLIENT, retenueInitialeMateriel } from '@shared/gamme.ts';

interface ProduitGamme {
  id: string; designation: string; reference: string; marque: string | null; famille: string | null; score: number;
  achats?: { prix_effectif: { prix: string | null } | null };
}
interface LigneMat {
  cle: string; categorie: string; libelle: string; fabricant: string | null; reference: string | null; quantite: string | null; unite: 'u' | 'm';
  prix: string | null; source: string; section: string; complement?: boolean;
}
interface VueMateriel {
  propositionId: string; libelle: string; statut: string; perimee: boolean; couts: boolean; lignes: LigneMat[];
  ma_gamme: Record<string, ProduitGamme[]> | null; droits: { ajouter: boolean };
}
interface DevisDossier { id: string; number: string; title: string; revisions: { id: string; rev_no: number; variant: string; status: string }[] }

const MODELE = CHOIX_MODELE;
const CATEGORIES: Record<string, string> = {
  unite_interieure: 'Unités intérieures', groupe_exterieur: 'Groupes extérieurs', derivation: 'Dérivations', tube: 'Cuivre', relevage: 'Relevage', regulation: 'Régulation',
};
const STATUTS_FIGES = ['emise', 'remplacee', 'annulee'];

export function MaterielVariante({ dossierId, propositionId }: { dossierId: string; propositionId: string }) {
  const qc = useQueryClient(); const notifier = useNotifier();
  const q = useQuery({ queryKey: ['proposition-materiel', dossierId, propositionId], queryFn: () => api.get<VueMateriel>(`/api/dossiers/${dossierId}/proposition/${propositionId}/materiel`) });
  const v = q.data;
  const ajouter = !!v?.droits.ajouter;
  const devis = useQuery({ queryKey: ['devis-du-dossier', dossierId], enabled: ajouter, queryFn: () => api.get<DevisDossier[]>(`/api/dossiers/${dossierId}/devis`) });
  const revisions = useMemo(() => (devis.data ?? []).flatMap((d) => d.revisions.filter((r) => !STATUTS_FIGES.includes(r.status))
    .map((r) => ({ valeur: r.id, libelle: `${d.number} — ${d.title} (révision ${r.rev_no}${r.variant !== 'Base' ? `, ${r.variant}` : ''})` }))), [devis.data]);
  const [choix, setChoix] = useState<Record<string, string>>({});
  const [retenues, setRetenues] = useState<Set<string>>(new Set());
  const [quantites, setQuantites] = useState<Record<string, string>>({});
  const [revision, setRevision] = useState('');
  const [envoi, setEnvoi] = useState(false);
  // Choix initial : le matériel de la variante (ligne d'appoint : « à choisir »), JAMAIS un produit Ma gamme (proposé, pas choisi).
  useEffect(() => {
    if (!v) return;
    setChoix(Object.fromEntries(v.lignes.map((l) => [l.cle, choixInitialMateriel(l)])));
    setQuantites(Object.fromEntries(v.lignes.map((l) => [l.cle, l.quantite ? String(Number(l.quantite)).replace('.', ',') : ''])));
    setRetenues(new Set(v.lignes.filter(retenueInitialeMateriel).map((l) => l.cle)));
  }, [v]);
  useEffect(() => { if (!revision && revisions.length === 1) setRevision(revisions[0].valeur); }, [revisions, revision]);

  if (q.isLoading) return <Chargement lignes={3} libelle="Chargement du matériel…" />;
  if (q.isError || !v) return <EtatErreur erreur={q.error} reessayer={() => q.refetch()} />;
  if (!v.lignes.length) return null;
  const nbGamme = v.lignes.filter((l) => (v.ma_gamme?.[l.cle]?.length ?? 0) > 0).length;
  const choisies = v.lignes.filter((l) => retenues.has(l.cle) && (quantites[l.cle] ?? '').trim() && !!choix[l.cle] && (choix[l.cle] !== MODELE || !l.complement));
  const envoyer = async () => {
    setEnvoi(true);
    try {
      const r = await api.post<{ creees: number; ignorees: number }>(`/api/dossiers/${dossierId}/proposition/${propositionId}/devis`, {
        revisionId: revision, lignes: choisies.map((l) => ({ cle: l.cle, gammeProduitId: choix[l.cle] === MODELE ? null : choix[l.cle], quantite: quantites[l.cle].trim() })) });
      await qc.invalidateQueries({ queryKey: ['devis-du-dossier', dossierId] });
      notifier(r.creees ? `${r.creees} ligne${r.creees > 1 ? 's' : ''} ajoutée${r.creees > 1 ? 's' : ''} au devis${r.ignorees ? ` (${r.ignorees} déjà présente${r.ignorees > 1 ? 's' : ''})` : ''}.`
        : 'Ces lignes sont déjà dans le devis : rien n’a été ajouté.');
    } catch (x) { notifier(messageErreur(x), true); } finally { setEnvoi(false); }
  };
  const devisChoisi = (devis.data ?? []).find((d) => d.revisions.some((r) => r.id === revision));

  return (
    <Panneau titre={`Matériel à chiffrer (${v.lignes.length})`} serre>
      <div className="panneau-corps pile prp-materiel">
        <p className="petit secondaire">Quantités comptées sur la variante « {v.libelle} » (rien n’est recalculé : implantation, réseau et diamètres restent ceux de la proposition).
          {v.ma_gamme && nbGamme > 0 ? ' Les produits de Ma gamme compatibles sont proposés en premier, sans être choisis à votre place.' : ''}</p>
        {ajouter && v.ma_gamme && nbGamme > 0 && <p className="tres-petit discret prp-mat-marque">{PHRASE_MARQUE_CLIENT}</p>}
        {v.perimee && <Alerte ton="ambre" titre="Proposition périmée">Les entrées ont changé depuis le calcul : relancez la proposition avant de chiffrer son matériel.</Alerte>}
        <ul className="prp-mat-lignes" aria-label="Matériel de la variante">
          {v.lignes.map((l) => {
            const gamme = v.ma_gamme?.[l.cle] ?? [];
            const options = [
              ...gamme.map((p) => ({ valeur: p.id, libelle: `${LIBELLE_PROPOSE_GAMME} — ${p.designation} (${p.reference})${p.achats?.prix_effectif?.prix ? ` — ${euros(p.achats.prix_effectif.prix)} HT` : ''}` })),
              ...(l.complement ? [] : [{ valeur: MODELE, libelle: `Matériel de la variante — ${l.reference ?? l.libelle}${v.couts && l.prix ? ` — ${euros(l.prix)} HT` : ''}` }]),
            ];
            const choisi = gamme.find((p) => p.id === choix[l.cle]);
            return (
              <li key={l.cle} className="prp-mat-ligne">
                <div className="prp-mat-tete">
                  {ajouter ? (
                    <label className="case prp-mat-case"><input type="checkbox" checked={retenues.has(l.cle)}
                      onChange={(e) => setRetenues((s) => { const n = new Set(s); if (e.target.checked) n.add(l.cle); else n.delete(l.cle); return n; })} />
                      <span><strong>{l.libelle}</strong></span></label>
                  ) : <strong>{l.libelle}</strong>}
                  <span className="rangee prp-mat-badges">
                    <Badge icone={false}>{CATEGORIES[l.categorie] ?? l.categorie}</Badge>
                    {gamme.length > 0 && <Badge ton="accent" icone={false}>{LIBELLE_PROPOSE_GAMME}</Badge>}
                  </span>
                </div>
                <span className="petit secondaire">{l.quantite !== null ? `${nombre(l.quantite, 2)} ${l.unite}` : 'Quantité à saisir'} — {l.source}
                  {l.fabricant && !choisi ? ` — ${l.fabricant}` : ''}</span>
                {!ajouter && gamme.length > 0 && (
                  <span className="petit">{LIBELLE_PROPOSE_GAMME} : {gamme.map((p) => `${p.designation} (${p.reference})`).join(' ; ')}</span>
                )}
                {ajouter && (
                  <div className="prp-mat-choix">
                    <ChampChoix libelle={`Matériel retenu — ${l.libelle}`} value={choix[l.cle] ?? choixInitialMateriel(l)} options={options} vide={l.complement ? 'À choisir' : undefined}
                      onChange={(e) => setChoix((c) => ({ ...c, [l.cle]: e.target.value }))} />
                    <Champ libelle={`Quantité (${l.unite})`} inputMode="decimal" value={quantites[l.cle] ?? ''} placeholder="À saisir"
                      onChange={(e) => setQuantites((x) => ({ ...x, [l.cle]: e.target.value }))} />
                  </div>
                )}
              </li>
            );
          })}
        </ul>
        {ajouter && (
          <div className="pile prp-mat-envoi">
            {revisions.length ? (
              <ChampChoix libelle="Devis du dossier (révision en brouillon)" vide="Choisir le devis" value={revision} options={revisions} onChange={(e) => setRevision(e.target.value)} />
            ) : devis.isLoading ? <Chargement lignes={1} /> : (
              <Alerte titre="Aucun devis modifiable">Créez d’abord un devis pour ce dossier dans <Link to={`/etudes/${dossierId}`}>le dossier</Link>.</Alerte>
            )}
            <div className="groupe-btn">
              <Bouton variante="principal" icone={<PackagePlus aria-hidden />} chargement={envoi} disabled={!revision || !choisies.length || v.perimee} onClick={envoyer}>
                {`Ajouter au devis (${choisies.length} ligne${choisies.length > 1 ? 's' : ''})`}</Bouton>
              {devisChoisi && <Link className="btn" to={`/etudes/${dossierId}/devis/${devisChoisi.id}?revision=${revision}`}>Ouvrir le devis</Link>}
            </div>
            <p className="tres-petit discret">Rejouer l’ajout n’ajoute rien en double. Un produit Ma gamme entre au devis sous la désignation, la marque et la référence de votre société.</p>
          </div>
        )}
      </div>
    </Panneau>
  );
}
